import React, { Dispatch, SetStateAction, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AssetCategory } from "velavu-js-api";
import AssetCategorization, {
	mapCategoryName,
	serializeAssetCategorization,
} from "../data/asset-categorization";
import IconDelete from "../dynamicicons/icon-delete";
import IconUnlink from "../dynamicicons/icon-unlink";
import * as styles from "./asset-filter-menu.module.scss";
import ChipFilterDivider from "./chip-filter-divider";
import ChipFilterMenu from "./chip-filter-menu";
import HoverTooltip from "./hover-tooltip";
import VelavuButton from "./velavu-button";
import VelavuSwitch from "./velavu-switch";
import ConnectionStatus from "../data/connection-status";
import { mapConnectionStatus } from "../helper/language-helper";
import whitelabel from "../whitelabel";
import StatusIndicator from "./status-indicator";
import { getPermissionDeniedMessage } from "../helper/permission-helper";

export default function AssetFilterMenu(props: {
	filterCategory: AssetCategory | null;
	setFilterCategory: Dispatch<SetStateAction<AssetCategory | null>>;
	categorizations: AssetCategorization[];
	filterCategorization: AssetCategorization | null;
	setFilterCategorization: Dispatch<
		SetStateAction<AssetCategorization | null>
	>;
	filterStatus: ConnectionStatus | null;
	setFilterStatus: Dispatch<SetStateAction<ConnectionStatus | null>>;
	filterUnpaired: boolean;
	setFilterUnpaired: Dispatch<SetStateAction<boolean>>;
	selectedAssets: string[];
	isDirty: boolean;
	onApply: VoidFunction;
	onUnpair: VoidFunction;
	onDelete: VoidFunction;
	canUnpair?: boolean;
	canDelete?: boolean;
}) {
	const { t } = useTranslation();

	const categoryLabel = useCallback(
		(category: AssetCategory | null) =>
			category === null
				? t("elements.filters.all")
				: mapCategoryName(category, t),
		[t],
	);

	const categorizationLabel = useCallback(
		(item: AssetCategorization | null) =>
			item === null ? t("elements.filters.all") : item.group,
		[t],
	);

	const statusLabel = useCallback(
		(status: ConnectionStatus | null) =>
			status === null
				? t("elements.filters.all")
				: mapConnectionStatus(status, t),
		[t],
	);

	return (
		<div className={styles.container}>
			{props.selectedAssets.length > 0 ? (
				<>
					<div className={styles.selectedAssets}>
						<label>{t("elements.filters.selected")}</label>
						<div className={styles.selectedAssetsTally}>
							<label>{props.selectedAssets.length}</label>
						</div>
					</div>
					<ChipFilterDivider />
					<div className={styles.actionButtons}>
						<HoverTooltip
							tooltip={
								!props.canUnpair
									? getPermissionDeniedMessage(
											"assets:write",
											t,
										)
									: undefined
							}
						>
							<button
								className={styles.unpairButton}
								onClick={props.onUnpair}
								disabled={!props.canUnpair}
							>
								<IconUnlink
									color={whitelabel.palette.secondary900}
									size={16}
								/>
								<span>{t("common.unpair")}</span>
							</button>
						</HoverTooltip>
						<HoverTooltip
							tooltip={
								!props.canDelete
									? getPermissionDeniedMessage(
											"assets:delete",
											t,
										)
									: undefined
							}
						>
							<button
								className={styles.deleteButton}
								onClick={props.onDelete}
								disabled={!props.canDelete}
							>
								<IconDelete
									color={whitelabel.palette.error900}
									size={16}
								/>
								<span>{t("common.delete")}</span>
							</button>
						</HoverTooltip>
					</div>
				</>
			) : (
				<>
					<ChipFilterMenu
						title={t("elements.filters.category")}
						options={[null, ...Object.values(AssetCategory)]}
						selected={props.filterCategory}
						setSelected={props.setFilterCategory}
						label={categoryLabel}
						category={(category) => category ?? undefined}
					/>
					<ChipFilterDivider />
					<ChipFilterMenu
						title={t("elements.filters.group")}
						options={[null, ...props.categorizations]}
						selected={props.filterCategorization}
						setSelected={props.setFilterCategorization}
						label={categorizationLabel}
						category={(item) =>
							item === null ? undefined : item.category
						}
						useDropdown
						keyExtractor={(item) =>
							item === null
								? null
								: serializeAssetCategorization(item)
						}
					/>
					<ChipFilterDivider />
					<ChipFilterMenu
						title={t("elements.filters.status")}
						options={[null, "active", "inactive", "registered"]}
						selected={props.filterStatus}
						setSelected={props.setFilterStatus}
						label={statusLabel}
						useDropdown
						dropdownMode="column"
						renderDropdownItem={(status) => (
							<div className={styles.assetFilterItem}>
								<StatusIndicator status={status!} />
								<span style={{ marginLeft: 8 }}>
									{mapConnectionStatus(status!, t)}
								</span>
							</div>
						)}
					/>
					<ChipFilterDivider />
					<VelavuSwitch
						className={styles.assetPairedSwitch}
						label={t("elements.filters.unpaired")}
						toggled={props.filterUnpaired}
						onChange={() =>
							props.setFilterUnpaired(!props.filterUnpaired)
						}
					/>
					{props.isDirty && (
						<VelavuButton
							variant="outlined"
							label={t("common.apply")}
							onClick={props.onApply}
							className={styles.applyFiltersButton}
						/>
					)}
				</>
			)}
		</div>
	);
}
